import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { X, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import '../components/AllCss/Toast.css';

const ToastContext = createContext(null);

const ICONS = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
};

function ToastItem({ toast, onDismiss }) {
  const Icon = ICONS[toast.type] || Info;
  return (
    <div className={`toast-item toast-item--${toast.type}`} role="status">
      <Icon size={20} className="toast-item-icon" aria-hidden />
      <span className="toast-item-message">{toast.message}</span>
      <button type="button" className="toast-item-close" onClick={() => onDismiss(toast.id)} aria-label="Dismiss">
        <X size={16} />
      </button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((message, { type = 'info', duration = 4500 } = {}) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setToasts((prev) => [...prev, { id, message, type }]);
    if (duration > 0) {
      setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  const value = useMemo(
    () => ({
      success: (message, opts) => show(message, { ...opts, type: 'success' }),
      error: (message, opts) => show(message, { ...opts, type: 'error' }),
      info: (message, opts) => show(message, { ...opts, type: 'info' }),
    }),
    [show]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-host" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

/** Imperative API for modules that cannot use hooks */
let toastApi = null;
export function setToastApi(api) {
  toastApi = api;
}
export const toast = {
  success: (msg, opts) => toastApi?.success(msg, opts),
  error: (msg, opts) => toastApi?.error(msg, opts),
  info: (msg, opts) => toastApi?.info(msg, opts),
};
